import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { queryKeys } from '../lib/query-client';

const DEFAULT_FEATURES = [
  'constructionPermits',
  'constructionVolunteers',
  'constructionMessaging',
  'constructionPrayer',
  'constructionMinistrySpaces',
  'engagement',
  'engagementEmail',
  'engagementSms',
  'media',
  'helpSupport',
  'aiChat',
];

interface FeaturesContextValue {
  features: string[];
  isLoaded: boolean;
}

const FeaturesContext = createContext<FeaturesContextValue>({
  features: [],
  isLoaded: false,
});

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const { data: features = [], isPending } = useQuery({
    queryKey: queryKeys.features(),
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ data: { features: string[] } }>('/features');
        return res.data.features;
      } catch {
        return DEFAULT_FEATURES;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  return (
    <FeaturesContext.Provider value={{ features, isLoaded: !isPending }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  return useContext(FeaturesContext);
}

export function FeatureGate({
  featureId,
  children,
  fallback = null,
}: {
  featureId: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { features, isLoaded } = useFeatures();

  if (!isLoaded) return null;
  if (!features.includes(featureId)) return <>{fallback}</>;
  return <>{children}</>;
}
